// DuckDB date-bucket expressions for time-series charts, plus the JS label
// formatter the axis and the zoom slider share.
//
// Package code by the same rule as `makeDrillScope`: **no table or column name is
// baked in** — the caller passes the date *expression* (`validFrom`,
// `sht.validFrom`, `DATE_TRUNC('week', x)`, …) and keeps its schema to itself
// (SKILL.md rules 1–2). `escapeSql` is applied to every value that reaches the
// SQL text; SQL fragments (the caller's `dateExpr`) are never escaped, by rule 6.

import { escapeSql } from '@rdrive/helper-sql';

/**
 * The bucket sizes a time-series chart understands, in the order the toolbar
 * renders them.
 * @type {readonly string[]}
 */
export const TIME_UNITS = ['day', 'week', 'month'];

/**
 * Normalise a `groupBy` value to a supported unit. Anything unrecognised — a
 * typo, `null`, `undefined` — becomes `'day'`, which is what both StatusHistory
 * configs did by hand before S11.
 *
 * @param {string} groupBy
 * @returns {'day'|'week'|'month'}
 */
export const timeUnitOf = (groupBy) => (TIME_UNITS.includes(groupBy) ? groupBy : 'day');

/**
 * `DATE_TRUNC(<unit>, <dateExpr>)` — the bucket a timestamp falls in.
 *
 * @param {'day'|'week'|'month'} unit
 * @param {string} dateExpr a SQL expression yielding a TIMESTAMP/DATE (NOT escaped — it is a fragment)
 * @returns {string} SQL
 */
export const truncSql = (unit, dateExpr) => `DATE_TRUNC('${timeUnitOf(unit)}', ${dateExpr})`;

/**
 * The bucket's string key: `YYYY-MM-DD` (day), `YYYY-Www` (week, `%V` = the ISO
 * week number) or `YYYY-MM` (month). This is the `timeKey` a chart row carries
 * and the key `bucketStartSql` parses back.
 *
 * **Week keys and the year.** By default a week key's year is the CALENDAR year
 * of the week's Monday (`%Y`), next to the ISO week number (`%V`). A late-December
 * Monday that starts ISO week 1 of the next year therefore gets the key of the
 * PREVIOUS January's week 1 (`2024-12-30` → `2024-W01`, the same key as
 * `2024-01-01`; `2025-12-29` → `2025-W01`), so the two buckets collide, sort out
 * of order, and `bucketStartSql` resolves the key to the wrong Monday. Pass
 * `{ isoYear: true }` for the ISO year (`%G`): every week then has its own key,
 * keys sort chronologically, and `bucketStartSql`'s ISO (Jan 4) anchor resolves
 * each key to its own Monday. Opt-in (IA_ABSORPTION_PLAN Phase 4 step 12c); both
 * apps pass it — issue-analysis's configs since 12c, project-overview's burndown,
 * velocity and legend since 2026-10-07 (Daniel). The `%Y` default stays only for
 * callers that never opted in.
 *
 * @param {'day'|'week'|'month'} unit
 * @param {string} dateExpr a SQL expression (NOT escaped — it is a fragment)
 * @param {{ isoYear?: boolean }} [options] `isoYear` — week keys carry the ISO year (`%G`)
 * @returns {string} SQL
 */
export const timeKeySql = (unit, dateExpr, { isoYear = false } = {}) => {
  const u = timeUnitOf(unit);
  if (u === 'month') return `strftime(${dateExpr}, '%Y-%m')`;
  if (u === 'week') return `strftime(${dateExpr}, '${isoYear ? '%G' : '%Y'}-W%V')`;
  return `strftime(${dateExpr}, '%Y-%m-%d')`;
};

/**
 * One bucket's width, for `GENERATE_SERIES`.
 *
 * @param {'day'|'week'|'month'} unit
 * @returns {string} `INTERVAL 1 DAY` | `INTERVAL 1 WEEK` | `INTERVAL 1 MONTH`
 */
export const intervalSql = (unit) => `INTERVAL 1 ${timeUnitOf(unit).toUpperCase()}`;

/**
 * The TIMESTAMP at which the bucket named by `timeKey` starts.
 *
 * Validates the key's shape and **throws** on a malformed one, so a caller has a
 * single failure mode to handle (the base chart catches per fetch and renders an
 * empty breakdown). Weeks use the **ISO anchor, Jan 4** — the rule the burndown
 * config already used; the velocity config's old `Jan 1 + 1 day + 7·(w−1)` then
 * `DATE_TRUNC('week', …)` landed a week early whenever Jan 1 fell on a
 * Friday–Sunday, so this helper is also the fix for that.
 *
 * @param {'day'|'week'|'month'} unit
 * @param {string} timeKey a key produced by {@link timeKeySql} for the same unit
 * @returns {string} SQL evaluating to a TIMESTAMP
 * @throws {Error} when `timeKey` does not match the unit's shape
 */
export const bucketStartSql = (unit, timeKey) => {
  const u = timeUnitOf(unit);
  const key = typeof timeKey === 'string' ? timeKey : '';

  if (u === 'month') {
    const m = /^(\d{4})-(\d{2})$/.exec(key);
    if (!m) throw new Error(`Invalid month key: ${String(timeKey)}`);
    return `CAST('${escapeSql(m[1])}-${escapeSql(m[2])}-01 00:00:00' AS TIMESTAMP)`;
  }

  if (u === 'week') {
    const m = /^(\d{4})-W(\d{1,2})$/.exec(key);
    if (!m) throw new Error(`Invalid week key: ${String(timeKey)}`);
    const week = parseInt(m[2], 10);
    if (!(week >= 1 && week <= 53)) throw new Error(`Invalid week number: ${String(timeKey)}`);
    const year = escapeSql(m[1]);
    const offsetDays = (week - 1) * 7;
    // ISO Monday of the requested week, 00:00
    return `
      CAST(
        DATE '${year}-01-04'
        + INTERVAL '${offsetDays}' DAY
        - INTERVAL ((EXTRACT(dow FROM DATE '${year}-01-04') + 6) % 7) DAY
        AS TIMESTAMP
      )`;
  }

  const m = /^(\d{4}-\d{2}-\d{2})$/.exec(key);
  if (!m) throw new Error(`Invalid day key: ${String(timeKey)}`);
  return `CAST('${escapeSql(m[1])} 00:00:00' AS TIMESTAMP)`;
};

/**
 * The calendar date (`YYYY-MM-DD`) on which the bucket named by `timeKey` starts — the JS twin
 * of {@link bucketStartSql}, for code that has to place a DATE on the bucket axis rather than
 * query it (BaseTimeSeriesChart's opt-in today marker, AM_ABSORPTION_PLAN step D4). The same
 * shapes and the same rule: a month starts on its 1st, a day is its key, a week on its ISO
 * Monday from the **Jan 4** anchor. Unlike the SQL helper it never throws — a malformed key, or
 * a week number outside 1–53, gives `null` (a marker is cosmetic, like a label).
 *
 * @param {'day'|'week'|'month'} unit
 * @param {string} timeKey a key produced by {@link timeKeySql} for the same unit
 * @returns {string|null} `YYYY-MM-DD`, or null for a malformed key
 */
export const bucketStartDate = (unit, timeKey) => {
  const u = timeUnitOf(unit);
  const key = typeof timeKey === 'string' ? timeKey : '';

  if (u === 'month') {
    const m = /^(\d{4})-(\d{2})$/.exec(key);
    return m ? `${m[1]}-${m[2]}-01` : null;
  }

  if (u === 'week') {
    const m = /^(\d{4})-W(\d{1,2})$/.exec(key);
    if (!m) return null;
    const week = parseInt(m[2], 10);
    if (!(week >= 1 && week <= 53)) return null;
    // Jan 4 of the key's year (setUTCFullYear: Date.UTC would read years 0–99 as 19xx), back
    // to its ISO Monday, then forward (week − 1) weeks — bucketStartSql's arithmetic
    const jan4 = new Date(0);
    jan4.setUTCFullYear(Number(m[1]), 0, 4);
    const sinceMonday = (jan4.getUTCDay() + 6) % 7;
    jan4.setUTCDate(jan4.getUTCDate() - sinceMonday + (week - 1) * 7);
    return jan4.toISOString().slice(0, 10);
  }

  const m = /^(\d{4}-\d{2}-\d{2})$/.exec(key);
  return m ? m[1] : null;
};

/**
 * The human label for a bucket key — used for the x-axis labels, the zoom
 * slider's edge labels and the tooltip header.
 *
 * `week` reads `${weekLabel} ${ww}, ${yyyy}` (the caller supplies the translated
 * word, so this stays framework-free); `month` uses `toLocaleString(locale, …)`
 * with the **dashboard's** locale, not the browser's; `day` is the key as-is.
 * An unparseable key falls through to the key itself rather than throwing —
 * a label is cosmetic, unlike the SQL above.
 *
 * @param {string} timeKey
 * @param {'day'|'week'|'month'} unit
 * @param {{ weekLabel?: string, locale?: string }} [options]
 * @returns {string}
 */
export const formatBucketLabel = (timeKey, unit, { weekLabel = 'Week', locale = 'en' } = {}) => {
  const u = timeUnitOf(unit);
  const key = typeof timeKey === 'string' ? timeKey : String(timeKey ?? '');

  if (u === 'week') {
    const m = /^(\d{4})-W(\d{1,2})$/.exec(key);
    if (!m) return key;
    return `${weekLabel} ${m[2]}, ${m[1]}`;
  }

  if (u === 'month') {
    const m = /^(\d{4})-(\d{2})$/.exec(key);
    if (!m) return key;
    return new Date(Number(m[1]), Number(m[2]) - 1).toLocaleString(locale, {
      month: 'long',
      year: 'numeric',
    });
  }

  return key;
};
